/**
 * SEO HEADING STRUCTURE - Homepage JP (IndexJp)
 * H1: 「一人でも、孤独じゃない。」 (Hero)
 * H2: 「リアルタイムのコラボレーション空間」 (LiveWorkspaces)
 * H2: 「深い集中のために設計」 (DeepFocus)
 * H2: 「学生のために」 (ForStudents)
 * H2: 「教育機関のために」 (ForInstitutions)
 * H2: 「コラボレーションと学習の行動レイヤー」 (BehavioralLayer)
 */

import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { LiveWorkspaces } from "@/components/home/LiveWorkspaces";
import { DeepFocus } from "@/components/home/DeepFocus";
import { ForStudents, ForInstitutions } from "@/components/home/AudienceSections";
import { BehavioralLayer } from "@/components/home/BehavioralLayer";
import { CTA } from "@/components/CTA";

const IndexJp = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="CSW - オンライン自習室 | バーチャル勉強部屋 | ボディダブリング | Study Stream"
        description="無料のオンライン自習室で集中力と学習習慣を高めましょう。ライブ配信、ポモドーロタイマー、世界中の学生とつながるボディダブリング・プラットフォーム。"
        keywords="オンライン自習室, バーチャル勉強部屋, オンライン勉強, 勉強配信, ボディダブリング, ポモドーロタイマー"
        canonicalUrl="/jp/"
      />
      <Header />

      <main>
        {/* Hero Section */}
        <section className="relative flex items-start justify-center pt-[57px] overflow-hidden" aria-labelledby="hero-title">
          <div className="absolute inset-0 bg-gradient-to-b from-background via-background to-muted" aria-hidden="true" />
          <div className="absolute top-20 left-20 w-96 h-96 bg-primary/20 rounded-full blur-[120px] animate-glow-pulse" aria-hidden="true" />
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-secondary/20 rounded-full blur-[120px] animate-glow-pulse" style={{ animationDelay: '1s' }} aria-hidden="true" />

          <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8 py-0">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <article className="text-left space-y-8 animate-fade-in">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-card/50 border border-border backdrop-blur-sm">
                  <div className="w-2 h-2 bg-primary rounded-full animate-glow-pulse" aria-hidden="true" />
                  <h1 id="hero-title" className="text-sm font-medium text-muted-foreground m-0">
                    オンライン自習室 • 永久無料 • 制限なし
                  </h1>
                </div>

                <p className="font-display italic text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight" style={{ fontWeight: 400 }}>
                  <span className="text-primary">一人でも、</span>
                  <br />
                  <span className="text-foreground">孤独じゃない。</span>
                </p>

                <p className="text-base sm:text-lg text-accent font-light max-w-xl leading-relaxed">
                  一緒に勉強。一緒に集中。
                </p>

                <p className="text-lg sm:text-xl text-muted-foreground font-light max-w-xl leading-relaxed">
                  CSWは、カメラをオンにして他の人と一緒に勉強できる無料のボディダブリング・プラットフォームです。ライブ配信、「Study with me」セッション、世界中の人々と繋がるバーチャルルームに参加しましょう — 一人じゃない方が、集中できるから。
                </p>

                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-4 w-full">
                  <Button
                    size="lg"
                    className="group bg-primary hover:bg-primary/90 text-primary-foreground font-sans font-light px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg glow-primary transition-all hover:scale-105 w-full sm:w-auto"
                    asChild
                  >
                    <a href="/students/">
                      学生の方へ
                      <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                    </a>
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    className="border-2 border-border hover:bg-card font-sans font-light px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg transition-all hover:scale-105 w-full sm:w-auto"
                    asChild
                  >
                    <a href="/institution/">
                      教育機関の方へ
                    </a>
                  </Button>
                </div>

                <aside className="flex flex-wrap items-center justify-center sm:justify-start gap-4 sm:gap-8 pt-8" aria-label="主要統計">
                  <div className="text-center sm:text-left">
                    <p className="text-2xl sm:text-3xl font-bold text-gradient">10,000+</p>
                    <p className="text-xs sm:text-sm text-muted-foreground">学習セッション</p>
                  </div>
                  <div className="w-px h-10 sm:h-12 bg-border hidden sm:block" aria-hidden="true" />
                  <div className="text-center sm:text-left">
                    <p className="text-2xl sm:text-3xl font-bold text-gradient">24/7</p>
                    <p className="text-xs sm:text-sm text-muted-foreground">ライブルーム</p>
                  </div>
                  <div className="w-px h-10 sm:h-12 bg-border hidden sm:block" aria-hidden="true" />
                  <div className="text-center sm:text-left">
                    <p className="text-2xl sm:text-3xl font-bold text-gradient">¥0</p>
                    <p className="text-xs sm:text-sm text-muted-foreground">永久無料</p>
                  </div>
                </aside>
              </article>

              <figure className="relative animate-fade-in" style={{ animationDelay: '0.2s' }}>
                <div className="relative rounded-3xl overflow-hidden border border-border/50 shadow-2xl">
                  <video
                    src="/videos/productivity-demo.mp4"
                    autoPlay loop muted playsInline
                    preload="metadata"
                    className="w-full h-auto"
                    aria-label="CSW 学習配信デモ"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" aria-hidden="true" />
                </div>
                <figcaption className="sr-only">CSW ボディダブリング・プラットフォーム</figcaption>
              </figure>
            </div>
          </div>
        </section>

        <LiveWorkspaces t={{
          eyebrow: "セクション 02 — ライブ・ワークスペース",
          title: <>リアルタイムの<em className="urgent-cyan">コラボレーション</em>空間。</>,
          body: "集中・生産性・コラボレーション・共に学ぶために設計された、オンライン空間を作成または参加できます。",
          panelHeader: "ライブ参加状況 · 24時間",
          panelActive: "2,481 アクティブ",
          features: [
            ["01", "ライブルーム", "アクティブなルームにいつでも参加。"],
            ["02", "ポモドーロタイマー", "集中と休憩を同期したサイクル。"],
            ["03", "共有ワークスペース", "持続的なオンライン空間で共同作業。"],
            ["04", "スタディストリーム", "カメラオンの集中セッション。"],
            ["05", "グループセッション", "仲間とリアルタイムで協力。"],
          ],
        }} />

        <DeepFocus t={{
          eyebrow: "セクション 03 — 生産性と集中",
          title: <><em className="urgent">深い集中</em>のために設計。</>,
          body: "フォーカスタイマー、全画面ワークモード、ストリーク記録、コラボレーション環境 — 一緒に生産性を維持するための機能が揃っています。",
          focusLabel: "集中時間 · 直近7日",
          focusValue: "604",
          streakLabel: "ストリーク · セッション",
          streakValue: "12日間",
          pillars: [
            ["01", "フォーカスタイマー", "ポモドーロとカスタム間隔。"],
            ["02", "全画面ワークモード", "気を散らさないディープワーク。"],
            ["03", "ストリーク記録", "積み重なる毎日の継続。"],
            ["04", "コラボレーション環境", "共有の責任感がある静かな部屋。"],
          ],
        }} />

        <ForStudents t={{
          eyebrow: "セクション 04 — 学生",
          title: <><em className="urgent-cyan">学生</em>のために。</>,
          body: "オンライン自習室、Study with me 環境、生産性ツール、コラボレーション学習空間。",
          cta: "学生向けスペースを見る",
          panelHeader: "学生ルーム · ライブ",
          panelCount: "312 中 5 件",
          rows: [
            ["マヤ · 生物 201", "ライブルーム", "00:42"],
            ["ジョーダン · 微積 II", "Study with me", "01:18"],
            ["プリヤ · 文学論文", "ポモドーロ 4/6", "00:23"],
            ["ルーカス · CS ラボ", "グループセッション", "02:05"],
            ["アイシャ · MCAT 対策", "ソロ集中", "03:11"],
          ],
        }} />

        <ForInstitutions t={{
          eyebrow: "セクション 05 — 教育機関",
          title: <><em className="urgent">教育機関</em>のために。</>,
          body: "学生の継続を支援するための、行動エンゲージメント分析と早期警告指標。",
          cta: "教育機関向け機能を見る",
          panelHeader: "コホート・エンゲージメント vs ベースライン · 12週間",
          panelTrend: "−18% 傾向",
          stats: [
            ["アクティブ", "1,284"],
            ["リスク指標", "42"],
            ["週間アドバイザー通知", "11"],
          ],
        }} />

        <BehavioralLayer t={{
          eyebrow: "セクション 06 — 大きなビジョン",
          title: <>コラボレーションと学習の<em className="urgent">行動レイヤー</em>。</>,
          body: "CSWはコラボレーション空間と行動エンゲージメント・インテリジェンスを組み合わせ、人々がどのように集中・協力・関与するかを長期的に理解します。",
          layer1: "コラボレーション・ワークスペース",
          layer2: "行動エンゲージメント・シグナル",
          layer3: "長期的・経時的コンテキスト",
        }} />

        <CTA t={{
          title: <>一緒に<span className="urgent-cyan">集中しませんか？</span></>,
          body: "ボディダブリング、ライブ・スタディストリーム、世界中のコミュニティ — 完全無料。",
          button: "今すぐ参加",
        }} />
      </main>

      <Footer />
    </div>
  );
};

export default IndexJp;
